/**
 * ReportEditor (BAR-60)
 *
 * Rich-text editor for user-authored report edits. Wraps TipTap with a
 * markdown-round-trip extension so the user never sees raw markdown
 * syntax for the formatting we model — headings, bold/italic, lists,
 * blockquotes, links, inline code.
 *
 * v1 limitation: citations of the form `[label]{ref:marker_id}` pass
 * through as plain text in edit mode. They re-render as styled chips
 * the moment the user exits edit mode (the read-mode renderer in
 * ReportView preprocesses them). A follow-up will add a custom TipTap
 * inline atom so citations show as chips inside the editor too.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, useEditorState, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Markdown } from 'tiptap-markdown';
import {
  Bold,
  Italic,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Link as LinkIcon,
} from 'lucide-react';
import DialogShell from '../ui/DialogShell';

const SAFE_LINK_URL_PATTERN = /^(https?:\/\/|\/(?!\/))/i;

interface ReportEditorProps {
  initialContent: string;
  onChange?: (markdown: string) => void;
}

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  ariaLabel: string;
  /** Optional keyboard shortcut shown in the hover tooltip, e.g. "⌘B". */
  shortcut?: string;
  children: React.ReactNode;
}

type MarkdownStorage = {
  markdown?: {
    getMarkdown?: () => unknown;
  };
};

let hasWarnedMissingMarkdownStorage = false;

function getMarkdownFromEditor(editor: Editor, context: string): string | null {
  const storage = editor.storage as MarkdownStorage;
  const getMarkdown = storage.markdown?.getMarkdown;
  if (typeof getMarkdown !== 'function') {
    if (process.env.NODE_ENV !== 'production' && !hasWarnedMissingMarkdownStorage) {
      console.warn(
        `[ReportEditor] TipTap Markdown storage is unavailable during ${context}; markdown read skipped.`,
      );
      hasWarnedMissingMarkdownStorage = true;
    }
    return null;
  }

  const markdown = getMarkdown();
  return typeof markdown === 'string' ? markdown : null;
}

function ToolbarButton({ onClick, active, disabled, ariaLabel, shortcut, children }: ToolbarButtonProps) {
  const tooltip = shortcut ? `${ariaLabel} (${shortcut})` : ariaLabel;
  // Active state shifts BOTH background and icon color so it can't be
  // confused with hover (which only tints the background). Using
  // primary-tint + primary fg gives a clearly stronger visual weight
  // than the warm hover wash, matching the convergent pattern in Word,
  // Notion, and Linear: active ≥ 2× the visual weight of hover.
  //
  // Note: no `onMouseDown={preventDefault}` here. The historical TipTap
  // preventDefault dance interfered with cursor placement on toggle —
  // the cursor would land on the wrong line, which surfaced as users
  // reporting "the bullet list starts one row down." BlockNote and
  // Novel both rely on `editor.chain().focus()` inside the command
  // chain instead, which is what every onClick below does.
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      aria-pressed={active}
      disabled={disabled}
      title={tooltip}
      className={`inline-flex items-center justify-center w-8 h-8 rounded-md text-xs transition-colors border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface ${
        active
          ? 'bg-primary text-on-primary border-primary hover:bg-primary-hover'
          : 'bg-transparent text-fg-secondary border-transparent hover:bg-bg-warm hover:text-fg'
      } ${disabled ? 'opacity-45 cursor-not-allowed pointer-events-none' : ''}`}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor }: { editor: Editor | null }) {
  // useEditorState subscribes to editor state changes (selection moves,
  // marks toggled, transactions) and re-renders this component when
  // any of the selected fields actually change value. Without it,
  // editor.isActive(...) is read once at mount time and the toolbar
  // never updates as the user moves the cursor — so Bold / Italic /
  // heading buttons stayed frozen on whatever was true at first paint.
  // This is the canonical TipTap v3 React pattern.
  const flags = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      isH1: e?.isActive('heading', { level: 1 }) ?? false,
      isH2: e?.isActive('heading', { level: 2 }) ?? false,
      isH3: e?.isActive('heading', { level: 3 }) ?? false,
      isBold: e?.isActive('bold') ?? false,
      isItalic: e?.isActive('italic') ?? false,
      isCode: e?.isActive('code') ?? false,
      isLink: e?.isActive('link') ?? false,
    }),
  });

  const linkDialogTitleId = React.useId();
  const linkInputRef = useRef<HTMLInputElement>(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkDraft, setLinkDraft] = useState('https://');
  const [linkError, setLinkError] = useState<string | null>(null);

  const openLinkDialog = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href as string | undefined;
    setLinkDraft(previous ?? 'https://');
    setLinkError(null);
    setLinkDialogOpen(true);
  }, [editor]);

  const closeLinkDialog = useCallback(() => {
    setLinkDialogOpen(false);
    setLinkError(null);
  }, []);

  const applyLink = useCallback(() => {
    if (!editor) return;
    const url = linkDraft.trim();
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      closeLinkDialog();
      return;
    }
    // Reject anything that isn't an http(s) or relative URL — guards
    // against `javascript:` / `data:` href XSS in case the Link
    // extension's validate config is bypassed (defense in depth).
    if (!SAFE_LINK_URL_PATTERN.test(url)) {
      setLinkError('Link URL must start with http://, https://, or a "/" path.');
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    closeLinkDialog();
  }, [closeLinkDialog, editor, linkDraft]);

  if (!editor || !flags) return null;

  // Keyboard shortcuts mirror TipTap StarterKit's default bindings — we
  // surface them in tooltips so users learn them passively. macOS-style
  // glyphs only; cross-platform detection isn't worth the complexity
  // for this internal tool.
  return (
    <>
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-surface border border-border-default rounded-md mb-3 sticky top-0 z-10">
        <ToolbarButton
          ariaLabel="Heading 1"
          shortcut="⌘⌥1"
          active={flags.isH1}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        >
          <Heading1 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          ariaLabel="Heading 2"
          shortcut="⌘⌥2"
          active={flags.isH2}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          ariaLabel="Heading 3"
          shortcut="⌘⌥3"
          active={flags.isH3}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <Heading3 className="w-4 h-4" />
        </ToolbarButton>
        <span className="w-px h-5 bg-divider mx-1.5" aria-hidden="true" />
        <ToolbarButton
          ariaLabel="Bold"
          shortcut="⌘B"
          active={flags.isBold}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          ariaLabel="Italic"
          shortcut="⌘I"
          active={flags.isItalic}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          ariaLabel="Inline code"
          shortcut="⌘E"
          active={flags.isCode}
          onClick={() => editor.chain().focus().toggleCode().run()}
        >
          <Code className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          ariaLabel={flags.isLink ? 'Edit link' : 'Add link'}
          active={flags.isLink}
          onClick={openLinkDialog}
        >
          <LinkIcon className="w-4 h-4" />
        </ToolbarButton>
        {/*
         * Bullet list / numbered list / blockquote buttons removed. Users
         * type `1.`, `2.`, `> ` etc. as plain text — no auto-conversion,
         * no toolbar toggles. The matching extensions are disabled in
         * StarterKit below, which also kills the input rules that were
         * silently transforming user-typed numbers into list items at
         * the wrong position.
         */}
      </div>
      <DialogShell
        open={linkDialogOpen}
        onClose={closeLinkDialog}
        labelledBy={linkDialogTitleId}
        backdropPolicy="static"
        initialFocusRef={linkInputRef}
        overlayClassName="flex items-center justify-center p-4"
        panelClassName="w-full max-w-md border border-border-default overflow-hidden"
        header={(
          <h2
            id={linkDialogTitleId}
            className="text-[length:var(--text-base)] font-semibold text-fg"
          >
            {flags.isLink ? 'Edit link' : 'Add link'}
          </h2>
        )}
        headerClassName="px-5 pt-5"
        bodyClassName="px-5 pt-3 pb-5"
        footer={(
          <>
            <button
              type="button"
              onClick={closeLinkDialog}
              className="px-3 py-1.5 text-[length:var(--text-sm)] font-medium rounded-md text-fg-secondary hover:bg-surface focus:outline-none focus-visible:shadow-bark-focus transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={applyLink}
              className="px-3 py-1.5 text-[length:var(--text-sm)] font-medium rounded-md bg-primary text-on-primary hover:bg-primary-hover focus:outline-none focus-visible:shadow-bark-focus transition-colors"
            >
              Apply
            </button>
          </>
        )}
        footerClassName="px-5 py-3 bg-bg-warm border-t border-divider flex items-center justify-end gap-2"
      >
        <label
          htmlFor={`${linkDialogTitleId}-url`}
          className="block text-[length:var(--text-sm)] font-medium text-fg"
        >
          Link URL
        </label>
        <input
          ref={linkInputRef}
          id={`${linkDialogTitleId}-url`}
          type="text"
          value={linkDraft}
          onChange={(event) => {
            setLinkDraft(event.target.value);
            if (linkError) setLinkError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              applyLink();
            }
          }}
          aria-invalid={linkError ? 'true' : undefined}
          aria-describedby={linkError ? `${linkDialogTitleId}-error` : undefined}
          className="mt-1.5 w-full rounded-md border border-border-default bg-surface px-3 py-2 text-[length:var(--text-sm)] text-fg focus:outline-none focus:border-accent focus:shadow-bark-focus"
        />
        <p className="mt-2 text-[length:var(--text-xs)] text-fg-tertiary">
          Use http://, https://, or a path beginning with /.
        </p>
        {linkError && (
          <p
            id={`${linkDialogTitleId}-error`}
            role="alert"
            className="mt-2 text-[length:var(--text-sm)] text-danger"
          >
            {linkError}
          </p>
        )}
      </DialogShell>
    </>
  );
}

// Public ref handle. `getMarkdown` is the only contract production
// callers need; `getEditor` is a debug escape hatch used by the
// /__test/editor sandbox to expose the live editor on window for
// Playwright probes — declared optional so production call sites
// (ReportView etc.) don't have to ack it.
export type ReportEditorHandle = {
  getMarkdown: () => string;
  getEditor?: () => Editor | null;
};

const ReportEditor = React.forwardRef<ReportEditorHandle, ReportEditorProps>(
  function ReportEditor({ initialContent, onChange }, ref) {
    // useEditor returns the editor instance directly; no separate
    // useState needed. The Toolbar's reactivity comes from
    // useEditorState, not from re-receiving a fresh prop reference.
    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          // StarterKit ships its own Link extension in v3 — we disable it
          // here so the standalone @tiptap/extension-link below is the
          // single source. Without `link: false`, TipTap warns about
          // duplicate extension names ('link') at editor mount.
          link: false,
          // Lists + blockquote are disabled entirely. We removed the
          // toolbar buttons and we don't want the input-rules either:
          // typing `1. ` should stay as the literal "1. text", not
          // auto-convert to a numbered list (which surfaces as "the
          // list ate my cursor"). Disabling these extensions kills
          // both the rendering and the input rules in one shot.
          // listItem also has to go because it's the child node type
          // for the two list extensions.
          bulletList: false,
          orderedList: false,
          listItem: false,
          blockquote: false,
        }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          // First line of defense against `javascript:` / `data:` URLs
          // sneaking in via paste / autolink / programmatic setLink.
          // The toolbar's link dialog also validates manually.
          validate: (href) => SAFE_LINK_URL_PATTERN.test(href),
          HTMLAttributes: {
            class: 'text-accent hover:text-accent-hover underline',
          },
        }),
        Markdown.configure({
          html: false,
          tightLists: true,
          linkify: false,
          breaks: false,
          transformPastedText: true,
          transformCopiedText: true,
        }),
      ],
      content: initialContent,
      onUpdate: ({ editor: e }) => {
        if (onChange) {
          const markdown = getMarkdownFromEditor(e, 'onUpdate');
          if (markdown !== null) onChange(markdown);
        }
      },
      editorProps: {
        attributes: {
          class:
            'prose-report focus:outline-none min-h-[300px] text-sm text-fg-secondary leading-relaxed',
        },
      },
    });

    React.useImperativeHandle(
      ref,
      () => ({
        getMarkdown: () => {
          if (!editor) return initialContent;
          const markdown = getMarkdownFromEditor(editor, 'getMarkdown');
          if (markdown === null) {
            throw new Error('Report editor markdown storage is unavailable');
          }
          return markdown;
        },
        // Escape hatch for the dev sandbox / Playwright probes — lets a
        // test script call editor.isActive(...) and editor.commands.*
        // directly to disambiguate "selection didn't move" from
        // "selection moved but the toolbar didn't re-render."
        getEditor: () => editor,
      }),
      [editor, initialContent],
    );

    useEffect(() => {
      return () => {
        editor?.destroy();
      };
    }, [editor]);

    return (
      <div>
        <Toolbar editor={editor} />
        <EditorContent editor={editor} />
      </div>
    );
  },
);

export default ReportEditor;
